/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState, useContext } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { object, bool, string, PropTypes } from 'prop-types';
import qs from 'qs';

// MUI
import {
    Grid, Checkbox,
} from '@material-ui/core';

// Helpers
import mbpLogger from 'mbp-logger';
import {
    addressTypeOptions, usStates, canStates, countries, getIsShowingLocationTypeField,
} from '../../Helpers/Helpers';
import { getFeatureFlag } from '../../../../../../../../../state/ducks/Checkout/ducks/Global/Global-Selectors';
import { normalizeArrayToObject } from '../../../../../../utils/object';
import { RecipientBookContext } from '../../Context/RecipientBookContext';

// API
import { qasSearchAddress } from '../../../../../../../../../apis/account-apis/RecipientBook-apis/qasSearchAddress';

// Components
import TextFieldHelper from '../../Helpers/TextFieldHelper';
import QasAddressConfirmationDialog from './QasAddressConfirmationDialog';
import { getCityStateFromZip } from '../../../../../../../../helpers/checkCityStateZip';

const AddMailingAddressForm = ({
    methods, classes, address, isViewingEditForm,
}) => {
    // Feature flags
    const isMyAccountQasOn = useSelector(getFeatureFlag('is-my-account-qas-on'));
    const isAccountUsingZipCodeSpecificDeliveryRoutes = useSelector(getFeatureFlag('is-account-using-zipcode-specific-delivery-routes'));

    const {
        setIsVerificationNeeded, confirmAddress, searchResults, setSearchResults, setFormatedSelectedAddress, setIsLoadingConfirmation, showSuggestedAddressDropdown, setShowSuggestedAddressDropdown,
    } = useContext(RecipientBookContext);

    const [selectedAddress, setSelectedAddress] = useState('');
    const [shouldMakeApiCall, setShoultMakeApiCall] = useState(false);
    const [apiError, setApiError] = useState(false);
    const watchLocationType = methods.watch('addressType') || address.addressType || '';
    // First condition checks whether the loaded address from API has an addressType of APO/FPO and if the RHF state has a value (RHF is undefined on load)
    // Second condition checks only if there is a value in RHF state (which will be populated after an onChange event)
    const isLocationTypeAPOFPO = (!methods.watch('addressType') && address.addressType === 'APO/FPO') || methods.watch('addressType') === 'APO/FPO' || '';
    const isLocationTypePOBox = (!methods.watch('addressType') && address.addressType === 'P.O.Box') || methods.watch('addressType') === 'P.O.Box' || '';
    const isShowingLocationTypeField = getIsShowingLocationTypeField(watchLocationType);

    const watchAddressLineOne = methods.watch('addressLineOne');
    const watchCountry = methods.watch('country');
    const zip = methods.watch('zip');
    // watchCountry value comes from selected country in the form when adding a recipient address
    const isAddNewRecipFormShowingCanadaStates = watchCountry === 'CAN' || watchCountry === 'CA' || watchCountry === 'CN' || watchCountry === 'Canada';
    // address country value come from the address object when editing a recipient address
    const isEditRecipAddressFormShowingCanadaStates = (address.country === 'CN' || address.country === 'CA' || address.country === 'CAN' || address.country === 'Canada') && !watchCountry
    const previousCountry = address.country || watchCountry || '';

    // data required to fetch QAS address
    const isoCountryCode = watchCountry === 'Canada' ? 'CAN' : 'USA';
    const params = {
        query: watchAddressLineOne,
        country: isoCountryCode,
        take: 25,
    };

    // To fetch search result
    const { data, status, error } = useQuery(
        ['qasSearchAddress', 'search', params],
        qasSearchAddress,
        {
            refetchOnWindowFocus: false,
            retry: false,
            enabled: isMyAccountQasOn && watchAddressLineOne?.length > 3 && shouldMakeApiCall,
        },
    );

    // To fetch selected address data
    const { data: formatData, status: formatStatus, error: formatError } = useQuery(
        ['formatAddress', 'format', selectedAddress],
        qasSearchAddress,
        {
            refetchOnWindowFocus: false,
            retry: false,
            enabled: selectedAddress !== '',
        },
    );

    useEffect(() => {
        if (status === 'error') {
            let searchResultsError = 'GENERIC_API_ERROR';
            if (error.message === 'Request failed with status code 404') {
                searchResultsError = 'NO_ADDRESS_FOUND';
            }
            mbpLogger.logError({
                function: 'qasSearchAddress',
                appName: process.env.npm_package_name,
                module: 'AddMailingAddressForm',
                message: `Error While calling qasSearchAddress, error is ${searchResultsError}`,
                error,
            });
            setIsVerificationNeeded(true);
            setIsLoadingConfirmation(false);
        }
        if (status === 'success' && data?.data?.results?.length > 0 && showSuggestedAddressDropdown) {
            setShoultMakeApiCall(false);
            setIsVerificationNeeded(true);
            setSearchResults(data.data.results);
        }
    }, [data, error, showSuggestedAddressDropdown]);

    useEffect(() => {
        const checkZip = async () => {
            if (zip && !zip.match(/[A-Za-z]/) && (zip.length === 5 || zip.match(/^\d{5}(?:-\d{4})?$/))) {
                const hasErrorMessage = await getCityStateFromZip(zip);
                if (hasErrorMessage) {
                    methods.setError('zip', { type: 'custom', message: 'Invalid Zip Code' });
                }
            }
        }
        checkZip();
    }, [zip]);

    const setFormValuesAndPerformAction = async (formattedAddress) => {
        Object.keys(formattedAddress).map((key) => methods.setValue(key, formattedAddress[key]));

        // In checkout, APO/FPO addresses have a PSC and Box Number
        // If there is a Bux number, it is sent in addressLineTwo, even though QAS provides it inside of addressLineOne
        // This custom logic takes the PSC and Box # - and places it to addressLineTwo
        if ((formattedAddress['city'] === 'APO' || formattedAddress['city'] === 'FPO')) {
            const regex = /(PSC \d+)( Box \d+)/;
            const matches = formattedAddress['addressLineOne'].match(regex);
            if (matches && matches.length) {
                const pscText = matches[1]; // "PSC 1"
                let boxText = matches[2]; // " Box 5"
                boxText = boxText.trim(); // Remove leading and trailing spaces
                methods.setValue('addressLineOne', pscText)
                methods.setValue('addressLineTwo', boxText)
            }
        }
        methods.trigger(['addressLineOne', 'addressLineTwo', 'city', 'state', 'zip', 'country']);
    };

    useEffect(() => {
        if (formatStatus === 'error') {
            setApiError('Oops, something went wrong, Please try again later!');
            mbpLogger.logError({
                function: 'qasFormatAddress',
                appName: process.env.npm_package_name,
                module: 'AddMailingAddressForm',
                message: 'Error While calling qasSearchAddress',
                formatError,
            });
            setIsLoadingConfirmation(false);
        }
        if (formatStatus === 'success' && formatData?.data?.address?.length > 0) {
            const { address, components } = formatData.data;
            const nComponents = normalizeArrayToObject(components);
            const nAddress = normalizeArrayToObject(address);

            const formatedAddress = {
                addressLineOne: nAddress.addressLine1,
                addressLineTwo: (nAddress.addressLine2 && nAddress.addressLine3) ? `${nAddress.addressLine2} ${nAddress.addressLine3}` : nAddress.addressLine2,
                city: nAddress.locality,
                state: nAddress.province,
                country: nComponents.countryISO1 === 'CAN' ? 'Canada' : 'United States',
                zip: isAccountUsingZipCodeSpecificDeliveryRoutes ? nAddress.postalCode : nAddress.postalCode.split('-')[0],
            };
            setFormatedSelectedAddress(formatedAddress);
            setFormValuesAndPerformAction(formatedAddress);
            setSelectedAddress('');
        }
    }, [formatData, formatError]);

    const handleSelectAnAddress = (address) => {
        setShowSuggestedAddressDropdown(false);
        const { format } = address;
        const formatParams = qs.parse(format.substring(format.indexOf('?') + 1));
        setSelectedAddress(formatParams);
    };
    if (confirmAddress) {
        return <QasAddressConfirmationDialog methods={methods} isShowingConfirmationDialog={true} handleSelectAnAddress={handleSelectAnAddress} classes={classes} apiError={apiError} />
    }
    return (
        <div style={!isViewingEditForm ? { marginTop: '24px' } : null}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextFieldHelper fieldName="country" optionsList={countries} fieldLabel="Country" required={false} methods={methods} address={address} previousCountry={previousCountry} />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12} lg={!isShowingLocationTypeField || !watchLocationType ? 12 : 6}>
                    <TextFieldHelper fieldName="addressType" optionsList={addressTypeOptions} fieldLabel="Location Type" required={false} methods={methods} address={address} />
                </Grid>
                {watchLocationType && isShowingLocationTypeField &&
                    <Grid item xs={12} lg={6}>
                        <TextFieldHelper fieldName="employerName" optionsList={false} fieldLabel={`${watchLocationType || ''} Name`} required={false} methods={methods} address={address} />
                    </Grid>
                }
            </Grid>
            <TextFieldHelper
                fieldName="addressLineOne"
                optionsList={false}
                fieldLabel={`${isLocationTypeAPOFPO ? "Unit/Ship/Psc Number" : isLocationTypePOBox ? "P.O. Box" : "Address"}`}
                required={false}
                methods={methods}
                address={address}
                searchResults={searchResults}
                showSuggestedAddressDropdown={showSuggestedAddressDropdown}
                handleSelectAnAddress={handleSelectAnAddress}
                setShoultMakeApiCall={setShoultMakeApiCall}
            />
            {(!isLocationTypePOBox) && (
                <TextFieldHelper
                    fieldName="addressLineTwo"
                    optionsList={false}
                    fieldLabel={isLocationTypeAPOFPO ? "Box/Hull Number" : "Address - Apt. #, Suite, Floor, Etc."}
                    required={false}
                    methods={methods}
                    address={address}
                />
            )}
            <Grid container spacing={2}>
                <Grid item xs={4}>
                    <TextFieldHelper fieldName="city" optionsList={false} fieldLabel={isLocationTypeAPOFPO ? "APO/FPO" : "City"} required={false} methods={methods} address={address} />
                </Grid>
                <Grid item xs={4}>
                    <TextFieldHelper fieldName="state" optionsList={isAddNewRecipFormShowingCanadaStates || isEditRecipAddressFormShowingCanadaStates ? canStates : usStates} fieldLabel={`${isAddNewRecipFormShowingCanadaStates || isEditRecipAddressFormShowingCanadaStates ? 'Province' : 'State'}`} required={false} methods={methods} address={address} />
                </Grid>
                <Grid item xs={4}>
                    <TextFieldHelper fieldName="zip" optionsList={false} fieldLabel="Zip Code" required={false} methods={methods} address={address} />
                </Grid>
            </Grid>
            {
                isViewingEditForm && address?.defaultKey !== '1'
                && (
                    <Grid container>
                        <Checkbox
                            icon={<span className={classes.customCheckbox} />}
                            checkedIcon={<span className={classes.customCheckedCheckbox} />}
                            color="primary"
                            classes={{ root: classes.facetsCheckbox }}
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...methods.register('defaultKey')}
                        />
                        Make this address as a default
                    </Grid>
                )
            }
        </div >
    );
};

AddMailingAddressForm.propTypes = {
    address: PropTypes.shape({
        addressBookEntryId: string,
        addressLineOne: string,
        addressType: string,
        city: string,
        country: string,
        firstName: string,
        isBillingAddress: bool,
        lastName: string,
        lastUpdateDate: string,
        phoneNumber: string,
        rcpId: string,
        state: string,
        zip: string,
    }),
    isViewingEditForm: bool,
    methods: object.isRequired,
    classes: object.isRequired,
};

AddMailingAddressForm.defaultProps = {
    address: {},
    isViewingEditForm: false,
};

export default AddMailingAddressForm;
